import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaCalendarDay, 
  FaTimes, 
  FaRegCalendarAlt 
} from 'react-icons/fa';
import { useTranslation } from "react-i18next";
import BookingModal from '../Booking/BookingModal';
import StatusBadge from '../Booking/StatusBadge';
import { getBookings } from '../../../../api/bookingApi';
import { formatISOStringToReadableDateOnly, formatISOStringToReadable, getLocalDateString } from '../../../../utils/dateUtils';
import { MODAL_MODES } from '../../../../constants/enums';
import '../css/BookingCalendar.css';

/* Helper: convert Date to local YYYY-MM-DD string */
const getLocalDateKey = (date) => {
  return (
    date.getFullYear() +
    '-' +
    String(date.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(date.getDate()).padStart(2, '0')
  );
};

const BookingCalendar = () => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [selectedDateBookings, setSelectedDateBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [showDateBookings, setShowDateBookings] = useState(false);

  /* -------------------------------------------------------------------------- */
  /*                               LOAD BOOKINGS                                */
  /* -------------------------------------------------------------------------- */

  const loadMonthBookings = useCallback(async (date) => {
    try {
      setLoading(true);
      setError(null);

      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const startDate = getLocalDateKey(firstDay);
      const endDate = getLocalDateKey(lastDay);

      const response = await getBookings({
        page: 0,
        size: 1000,
        startDate,
        endDate,
      });

      if (response && Array.isArray(response.content)) {
        setBookings(response.content);
      } else {
        setBookings([]);
      }
    } catch (err) {
      console.error('Error loading month bookings:', err);
      setError(t("admin.booking_calendar.error_load"));
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadDateBookings = useCallback(async (date) => {
    try {
      setLoading(true);
      const dateStr = getLocalDateKey(date);

      const response = await getBookings({
        page: 0,
        size: 100,
        date: dateStr,
      });

      if (response && Array.isArray(response.content)) {
        setSelectedDateBookings(response.content);
      } else {
        setSelectedDateBookings([]);
      }
    } catch (err) {
      console.error('Error loading date bookings:', err);
      setSelectedDateBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMonthBookings(currentDate);
  }, [currentDate, loadMonthBookings]);

  /* -------------------------------------------------------------------------- */
  /*                             CALENDAR GENERATION                            */
  /* -------------------------------------------------------------------------- */

  const getBookingsForDate = (date) => {
    const dateKey = getLocalDateKey(date);
    return bookings.filter((booking) => {
      const bookingDate = new Date(booking.bookingAt);
      const bookingKey = getLocalDateKey(bookingDate);
      return bookingKey === dateKey;
    });
  };

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const dayBookings = getBookingsForDate(current);
      days.push({
        date: new Date(current),
        isCurrentMonth: current.getMonth() === month,
        isToday: getLocalDateKey(current) === getLocalDateKey(new Date()),
        bookingCount: dayBookings.length,
        bookings: dayBookings,
      });
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  /* -------------------------------------------------------------------------- */
  /*                              EVENT HANDLERS                                */
  /* -------------------------------------------------------------------------- */

  const handleDateClick = async (day) => {
    setSelectedDate(day.date);
    setShowDateBookings(true);
    await loadDateBookings(day.date);
  };

  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    setModalMode(MODAL_MODES.VIEW);
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => setCurrentDate(new Date());

  const handleCloseModal = () => {
    setSelectedBooking(null);
    setModalMode(null);
  };

  const handleCloseDateBookings = () => {
    setShowDateBookings(false);
    setSelectedDate(null);
    setSelectedDateBookings([]);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const calendarDays = generateCalendarDays();

  /* -------------------------------------------------------------------------- */
  /*                                 RENDER UI                                  */
  /* -------------------------------------------------------------------------- */

  return (
    <div className="page-container">
      <div className="calendar-header">
        <h1>{t("admin.booking_calendar.title")}</h1>
        <p>{t("admin.booking_calendar.subtitle")}</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="calendar-container">
        {/* Calendar Navigation */}
        <div className="calendar-controls">
          <button className="btn btn-secondary" onClick={handlePrevMonth}>
            <FaChevronLeft /> {t("admin.booking_calendar.previous")}
          </button>
          <div className="calendar-title">
            <h2>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
          </div>
          <button className="btn btn-secondary" onClick={handleNextMonth}>
            {t("admin.booking_calendar.next")} <FaChevronRight />
          </button>
        </div>

        <div className="calendar-actions">
          <button className="btn btn-primary" onClick={handleToday}>
            <FaCalendarDay style={{ marginRight: '6px' }} /> {t("admin.booking_calendar.today")}
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="calendar-grid">
          <div className="calendar-weekdays">
            {dayNames.map((day) => (
              <div key={day} className="weekday-header">{day}</div>
            ))}
          </div>

          <div className="calendar-days">
            {calendarDays.map((day, index) => (
              <div
                key={index}
                className={`calendar-day
                  ${!day.isCurrentMonth ? 'other-month' : ''}
                  ${day.isToday ? 'today' : ''}
                  ${day.bookingCount > 0 ? 'has-bookings' : ''}`}
                onClick={() => handleDateClick(day)}
              >
                <div className="day-number">{day.date.getDate()}</div>
                {day.bookingCount > 0 && (
                  <div className="booking-indicator">
                    <span className="booking-count">{day.bookingCount}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="loading-message">{t("admin.booking_calendar.loading")}</div>
          </div>
        )}
      </div>

      {/* Modal: Date Bookings */}
      {showDateBookings && (
        <div className="modal-overlay" onClick={handleCloseDateBookings}>
          <div className="modal-wrapper" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h2>
                  {t("admin.booking_calendar.bookings_for", {
                    date: selectedDate ? formatISOStringToReadableDateOnly(getLocalDateString(selectedDate)) : ""
                  })}
                </h2>
                <button className="close-btn" onClick={handleCloseDateBookings}>
                  <FaTimes />
                </button>
              </div>

              <div className="modal-body">
                {loading ? (
                  <div className="loading-message">Loading bookings...</div>
                ) : selectedDateBookings.length > 0 ? (
                  <div className="date-bookings-list">
                    {selectedDateBookings.map((booking) => (
                      <div
                        key={booking.bookingId}
                        className="booking-item"
                        onClick={() => handleBookingClick(booking)}
                      >
                        <div className="booking-item-header">
                          <span className="booking-id">#{booking.bookingId}</span>
                          <StatusBadge status={booking.status} />
                        </div>
                        <div className="booking-item-details">
                          <div className="customer-info">
                            <span className="customer-name">{booking.customer.customerName}</span>
                            <span className="customer-phone">{booking.customer.phoneNumber}</span>
                          </div>
                          <div className="booking-info">
                            <span className="booking-time">{formatISOStringToReadable(booking.bookingAt)}</span>
                            <span className="guest-count">
                              {t("admin.booking_calendar.guests", { count: booking.numGuests })}
                            </span>
                          </div>
                        </div>
                        {booking.tables?.length > 0 && (
                          <div className="table-info">
                            {t("admin.booking_calendar.tables")}: {booking.tables.map((t) => t.tableName).join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-bookings-message">
                    <FaRegCalendarAlt className="no-bookings-icon" />
                    <h3>{t("admin.booking_calendar.no_bookings_title")}</h3>
                    <p>{t("admin.booking_calendar.no_bookings_message")}</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseDateBookings}>
                  {t("admin.booking_calendar.close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Booking Details */}
      {selectedBooking && modalMode && (
        <BookingModal
          booking={selectedBooking}
          mode={modalMode}
          onClose={handleCloseModal}
          onSave={() => {
            loadMonthBookings(currentDate);
            if (selectedDate) loadDateBookings(selectedDate);
            handleCloseModal();
          }}
        />
      )}
    </div>
  );
};

export default BookingCalendar;
